import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import PropertyImage from '../../components/PropertyImage';
import { createOrGetChat } from '../../lib/chat/createOrGetChat';
import { createTerminationRequest, updateTerminationRequest, markTerminationViewed } from '../../services/terminationRequests.service';
import { TERMINATION_STATUS_LABELS, TERMINATION_STATUS_STYLES, TERMINATION_REASON_OPTIONS, formatDate as formatTerminationDate } from '../../utils/termination';
import { mergeNotificationDocs } from '../../utils/tenantNotificationBuckets';
import { markNotificationsByBucketAsRead } from '../../utils/notifications';
import { 
  Home,
  Euro,
  CreditCard,
  FileText,
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Edit3,
  Send,
  MessageCircle,
  Wrench,
  Droplet,
  Zap,
  Shield,
  Building,
  User,
  Mail,
  Phone,
  ArrowRight,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ShieldAlert,
  Archive,
  PenTool
} from 'lucide-react';
import toast from 'react-hot-toast';

const RESOLVED_NOTIFICATION_ACTIONS = new Set([
  'accepted',
  'declined',
  'resolved',
  'completed',
  'done',
  'dismissed'
]);

const RESOLVED_NOTIFICATION_STATUSES = new Set([
  'resolved',
  'completed',
  'done',
  'closed',
  'cancelled',
  'declined'
]);

const ACTION_REQUIRED_NOTIFICATION_TYPES = new Set([
  'visit_request',
  'contract_required',
  'document_signature_required',
  'maintenance_confirmation',
  'payment_action_required',
  'amendment_proposed',
  'amendment_accepted',
]);

const NON_ACTIONABLE_NOTIFICATION_TYPES = new Set([
  'message',
  'info',
  'announcement',
  'system',
  'chat_message'
]);

function isNotificationPending(notification) {
  if (!notification) return false;
  if (notification.read) return false;
  if (notification.action && RESOLVED_NOTIFICATION_ACTIONS.has(notification.action)) return false;
  if (notification.status && RESOLVED_NOTIFICATION_STATUSES.has(notification.status)) return false;
  return true;
}

function isNotificationActionRequired(notification) {
  if (!notification) return false;
  if (!isNotificationPending(notification)) return false;

  const type = (notification.type || '').toLowerCase();
  const requiresActionFlag = notification.requiresAction === true || notification.actionRequired === true;
  const hasActionButton = Boolean(notification.actionLabel || notification.actionUrl || notification.ctaLabel || notification.ctaUrl);

  if (NON_ACTIONABLE_NOTIFICATION_TYPES.has(type)) return false;
  if (requiresActionFlag) return true;
  if (ACTION_REQUIRED_NOTIFICATION_TYPES.has(type)) return true;
  if (hasActionButton) return true;

  return false;
}

function getContractDisplayStatus(contract) {
  if (!contract) return { label: 'Onbekend', color: 'text-gray-600', bg: 'bg-gray-100 text-gray-800' };

  if (contract.terminationState === 'terminated') {
    return { label: 'Beëindigd', color: 'text-red-600', bg: 'bg-red-100 text-red-800' };
  }
  if (contract.terminationState === 'terminationScheduled') {
    return { label: 'Aflopend', color: 'text-orange-600', bg: 'bg-orange-100 text-orange-800' };
  }

  const now = new Date();
  const endDate = contract.endDate instanceof Date ? contract.endDate
    : (contract.endDate?.toDate ? contract.endDate.toDate() : null);

  if (endDate && endDate < now) {
    return { label: 'Afgelopen', color: 'text-red-600', bg: 'bg-red-100 text-red-800' };
  }

  const startDate = contract.startDate instanceof Date ? contract.startDate
    : (contract.startDate?.toDate ? contract.startDate.toDate() : null);

  if (startDate && startDate > now) {
    return { label: 'Gepland', color: 'text-blue-600', bg: 'bg-blue-100 text-blue-800' };
  }

  return { label: 'Actief', color: 'text-green-600', bg: 'bg-green-100 text-green-800' };
}

export default function MijnPandScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { propertyId: routePropertyId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [properties, setProperties] = useState([]); // Array of { contract, property, notificationCount }
  const [archivedProperties, setArchivedProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(routePropertyId || null);
  const [property, setProperty] = useState(null);
  const [contract, setContract] = useState(null);
  const [archivedContractsForProperty, setArchivedContractsForProperty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]); // All notifications for counting
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [myProblems, setMyProblems] = useState([]);
  const [showNotificationDetail, setShowNotificationDetail] = useState(null);
  const [activeTermination, setActiveTermination] = useState(null);

  const markNotificationHandledLocally = (notification, action = 'handled') => {
    if (!notification) return;

    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    setAllNotifications(prev =>
      prev.map(n =>
        n.id === notification.id
          ? { ...n, read: true, action: n.action || action, readAt: new Date() }
          : n
      )
    );
    setProperties(prevProperties =>
      prevProperties.map(prop => {
        const matchesProperty = notification.propertyId === prop.property?.id ||
          notification.propertyId === prop.contract?.propertyId ||
          notification.propertyId === prop.propertyId;

        if (!matchesProperty || !prop.notificationCount) return prop;

        return {
          ...prop,
          notificationCount: Math.max(0, prop.notificationCount - 1)
        };
      })
    );
  };


  useEffect(() => {
    if (user) {
      loadData();
      markNotificationsByBucketAsRead(user.uid, 'mijnPand');
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const unsubscribe = subscribeToNotifications();
      return unsubscribe;
    }
  }, [user]);

  useEffect(() => {
    if (routePropertyId && routePropertyId !== selectedPropertyId) {
      setSelectedPropertyId(routePropertyId);
    }
  }, [routePropertyId]);

  // Load termination requests for the selected contract (real-time)
  useEffect(() => {
    if (!contract?.id || !user?.uid) { setActiveTermination(null); return; }
    const trCol = collection(db, 'terminationRequests');
    const q1 = query(trCol, where('contractId', '==', contract.id), where('initiatorUserId', '==', user.uid));
    const q2 = query(trCol, where('contractId', '==', contract.id), where('counterpartyUserId', '==', user.uid));
    
    let docs1 = [], docs2 = [];
    const merge = () => {
      const seen = new Set();
      const all = [];
      for (const d of [...docs1, ...docs2]) {
        if (!seen.has(d.id)) { seen.add(d.id); all.push(d); }
      }
      const active = all.find(r =>
        ['requested', 'counter_proposed', 'accepted_by_tenant', 'accepted_by_landlord', 'mutual_acceptance_reached', 'completed'].includes(r.status)
      );
      setActiveTermination(active || null);
    };
    const unsub1 = onSnapshot(q1, (snap) => {
      docs1 = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      merge();
    }, (err) => { console.error('Termination subscription q1 error:', err.code, err.message); });
    const unsub2 = onSnapshot(q2, (snap) => {
      docs2 = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      merge();
    }, (err) => { console.error('Termination subscription q2 error:', err.code, err.message); });
    return () => { unsub1(); unsub2(); };
  }, [contract?.id, user?.uid]);

  // Real-time contract status subscription
  useEffect(() => {
    if (!contract?.id) return;
    const unsub = onSnapshot(doc(db, 'contracts', contract.id), (snap) => {
      if (!snap.exists()) return;
      const updated = snap.data();
      setContract(prev => {
        if (!prev || prev.id !== contract.id) return prev;
        return {
          ...prev,
          status: updated.status || prev.status,
          terminationState: updated.terminationState || prev.terminationState,
          terminationEffectiveDate: updated.terminationEffectiveDate?.toDate
            ? updated.terminationEffectiveDate.toDate()
            : (updated.terminationEffectiveDate instanceof Date ? updated.terminationEffectiveDate : prev.terminationEffectiveDate)
        };
      });
    }, (err) => { console.error('Contract subscription error:', err.code, err.message); });
    return () => unsub();
  }, [contract?.id]);
  
  useEffect(() => {
    // When a property is selected, update current property and contract
    if (selectedPropertyId && properties.length > 0) {
      const selected = properties.find(p => p.property.id === selectedPropertyId);
      if (selected) {
        setProperty(selected.property);
        setContract(selected.contract);
        setArchivedContractsForProperty(selected.archivedContracts || []);
        // Filter notifications for selected property
        const propertyNotifications = allNotifications.filter(
          n => n.propertyId === selectedPropertyId || n.propertyId === selected.contract.propertyId
        );
        setNotifications(propertyNotifications.filter(isNotificationActionRequired));

        // Load property reports for this property
        if (user?.uid) {
          getDocs(query(
            collection(db, 'propertyReports'),
            where('tenantId', '==', user.uid),
            where('propertyId', '==', selectedPropertyId)
          )).then((snap) => {
            const problems = snap.docs.map(d => ({
              id: d.id,
              ...d.data(),
              createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(d.data().createdAt || 0),
              respondedAt: d.data().respondedAt?.toDate ? d.data().respondedAt.toDate() : (d.data().respondedAt ? new Date(d.data().respondedAt) : null),
            }));
            problems.sort((a, b) => b.createdAt - a.createdAt);
            setMyProblems(problems);
          }).catch(() => setMyProblems([]));
        }
      } else {
        console.warn('Property not found in properties list, trying to load directly:', selectedPropertyId);
      }
    } else if (selectedPropertyId && properties.length === 0 && !loading) {
      console.log('Waiting for properties to load...');
    }
  }, [selectedPropertyId, properties, allNotifications, loading]);

  // Auto-mark responded problems as read when the tenant opens the Problems tab
  useEffect(() => {
    if (activeTab !== 'problems') return;
    const unread = myProblems.filter(p => p.status === 'responded' && !p.readByTenant);
    if (unread.length === 0) return;
    unread.forEach((p) => {
      updateDoc(doc(db, 'propertyReports', p.id), { readByTenant: true }).catch(() => {});
    });
    setMyProblems(prev => prev.map(p =>
      p.status === 'responded' && !p.readByTenant ? { ...p, readByTenant: true } : p
    ));
  }, [activeTab, myProblems.length]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (!user) {
        setLoading(false);
        return;
      }
      
      // Load contracts from Firestore
      // Try both tenantID and tenantId fields for backwards compatibility
      // Remove status filter from query to avoid index requirement - filter client-side instead
      const contractsQuery1 = query(
        collection(db, 'contracts'),
        where('tenantID', '==', user.uid)
      );
      
      const contractsQuery2 = query(
        collection(db, 'contracts'),
        where('tenantId', '==', user.uid)
      );
      
      // Execute both queries
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(contractsQuery1).catch(err => {
          console.log('Query 1 (tenantID) failed:', err);
          return { docs: [] };
        }),
        getDocs(contractsQuery2).catch(err => {
          console.log('Query 2 (tenantId) failed:', err);
          return { docs: [] };
        })
      ]);
      
      // Combine results and remove duplicates
      const allDocs = [...snapshot1.docs, ...snapshot2.docs];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );
      
      // Map to data and filter by status client-side
      const allContracts = uniqueDocs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter by status client-side (to avoid index requirement)
      const validStatuses = ['fullySigned', 'signedByTenant', 'signedByLandlord', 'sentToTenant', 'terminated', 'archived'];
      const contractsData = allContracts.filter(c => 
        validStatuses.includes(c.status)
      );
      
      console.log(`✅ Found ${contractsData.length} contracts for tenant ${user.uid} (out of ${allContracts.length} total)`);
      console.log('📋 Contract details:', contractsData.map(c => ({
        id: c.id,
        status: c.status,
        tenantID: c.tenantID,
        tenantId: c.tenantId,
        landlordID: c.landlordID
      })));
      
      // Load properties for each contract
      const propertiesWithCounts = await Promise.all(
        contractsData.map(async (contractData) => {
          // Get property ID - try multiple field names
          const propertyId = contractData.propertyId || 
                            contractData.kotID || 
                            contractData.core?.kotID || 
                            contractData.kotId;
          
          if (!propertyId) {
            console.warn('Contract has no property ID:', contractData.id, contractData);
            return null;
          }
          
          // Get property
          let propertyDoc;
          try {
            propertyDoc = await getDoc(doc(db, 'koten', propertyId));
          } catch (propError) {
            console.warn('Could not load property (permission denied or missing):', propertyId, propError.code);
            return null;
          }
          if (!propertyDoc.exists()) {
            console.warn('Property not found:', propertyId);
            return null;
          }
          
          const propertyData = {
            id: propertyDoc.id,
            ...propertyDoc.data()
          };
          
          // Count unread problem responses for this property
          let unreadProblemCount = 0;
          try {
            const problemSnap = await getDocs(query(
              collection(db, 'propertyReports'),
              where('tenantId', '==', user.uid),
              where('propertyId', '==', propertyId)
            ));
            unreadProblemCount = problemSnap.docs.filter(d => {
              const data = d.data();
              return data.status === 'responded' && !data.readByTenant;
            }).length;
          } catch (e) { /* ignore */ }
          const notificationCount = unreadProblemCount;
          
          // Format contract data
          const contract = {
            id: contractData.id,
            propertyId: propertyDoc.id,
            monthlyRent: contractData.core?.rent || contractData.rent || 0,
            deposit: contractData.core?.deposit || contractData.deposit || 0,
            paymentMethod: contractData.paymentMethod || 'bank_transfer',
            startDate: (contractData.core?.startDate?.toDate ? contractData.core.startDate.toDate() : 
                       (contractData.core?.startDate instanceof Date ? contractData.core.startDate :
                       (contractData.startDate?.toDate ? contractData.startDate.toDate() :
                       (contractData.startDate instanceof Date ? contractData.startDate : new Date())))),
            endDate: (contractData.core?.endDate?.toDate ? contractData.core.endDate.toDate() : 
                     (contractData.core?.endDate instanceof Date ? contractData.core.endDate :
                     (contractData.endDate?.toDate ? contractData.endDate.toDate() :
                     (contractData.endDate instanceof Date ? contractData.endDate : new Date())))),
            contractType: contractData.contractType || 'fixed',
            status: contractData.status || 'active',
            landlordId: contractData.core?.landlordID || contractData.landlordID || '',
            iban: contractData.core?.iban || contractData.iban || '',
            paymentReference: contractData.core?.paymentReference || contractData.paymentReference || '',
            depositPaymentReference: contractData.core?.depositPaymentReference || contractData.depositPaymentReference || '',
            depositPaymentInstructions: contractData.core?.depositPaymentInstructions || contractData.depositPaymentInstructions || '',
            voorschotPaymentInstructions: contractData.core?.voorschotPaymentInstructions || contractData.voorschotPaymentInstructions || '',
            voorschotAmount: contractData.voorschot?.amount ? (contractData.voorschot.amount / 100) : (contractData.voorschotAmount || 0),
            keyHandoverInstructions: contractData.core?.keyHandoverInstructions || contractData.keyHandoverInstructions || '',
            bankAccount: contractData.bankAccount || '',
            terminationState: contractData.terminationState || null,
            terminationEffectiveDate: contractData.terminationEffectiveDate?.toDate ? contractData.terminationEffectiveDate.toDate() : (contractData.terminationEffectiveDate instanceof Date ? contractData.terminationEffectiveDate : null)
          };
          
          // Get landlord info
          if (contract.landlordId) {
            try {
              const landlordDoc = await getDoc(doc(db, 'users', contract.landlordId));
              if (landlordDoc.exists()) {
                const landlordData = landlordDoc.data();
                contract.landlordName = landlordData.displayName || landlordData.name || 'Verhuurder';
                contract.landlordEmail = landlordData.email || '';
                contract.landlordPhone = landlordData.phone || '';
              }
            } catch (error) {
              console.error('Error loading landlord info:', error);
            }
          }
          
          return {
            contract,
            property: propertyData,
            notificationCount,
            _unreadProblemCount: unreadProblemCount
          };
        })
      );
      
      // Filter out null values
      const validProperties = propertiesWithCounts.filter(p => p !== null);

      // Group by property: collect archived contract versions alongside the active one
      const propertyMap = new Map();
      for (const entry of validProperties) {
        const pid = entry.property.id;
        if (!propertyMap.has(pid)) {
          propertyMap.set(pid, { ...entry, archivedContracts: [] });
        } else {
          const existing = propertyMap.get(pid);
          if (entry.contract.status === 'archived') {
            existing.archivedContracts.push(entry.contract);
          } else if (existing.contract.status === 'archived') {
            existing.archivedContracts.push(existing.contract);
            existing.contract = entry.contract;
          } else {
            const entryDate = entry.contract.startDate instanceof Date ? entry.contract.startDate : new Date(0);
            const existDate = existing.contract.startDate instanceof Date ? existing.contract.startDate : new Date(0);
            if (entryDate > existDate) {
              existing.archivedContracts.push(existing.contract);
              existing.contract = entry.contract;
            } else {
              existing.archivedContracts.push(entry.contract);
            }
          }
        }
      }
      const grouped = Array.from(propertyMap.values());

      // Separate active from archived panden (not contract versions)
      const isArchivedProperty = (entry) => {
        const c = entry.contract;
        const p = entry.property;
        if (c.status === 'terminated' || c.terminationState === 'completed' || c.terminationState === 'terminated') return true;
        if (p.status === 'archived') return true;
        const endDate = c.endDate instanceof Date ? c.endDate : (c.endDate?.toDate ? c.endDate.toDate() : null);
        if (endDate && endDate < new Date()) return true;
        return false;
      };

      const active = grouped.filter(p => !isArchivedProperty(p));
      const archived = grouped.filter(p => isArchivedProperty(p));

      setProperties(active);
      setArchivedProperties(archived);

      // If only one active property, select it automatically (no menu needed)
      if (active.length === 1) {
        setSelectedPropertyId(active[0].property.id);
        setProperty(active[0].property);
        setContract(active[0].contract);
        setArchivedContractsForProperty(active[0].archivedContracts || []);
      }
      // If multiple properties, don't auto-select (show menu)
      // If no properties, selectedPropertyId stays null
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading data:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      toast.error('Fout bij het laden van contracten: ' + (error.message || 'Onbekende fout'));
      setLoading(false);
    }
  };

  const subscribeToNotifications = () => {
    if (!user) return () => {};

    // Merge legacy tenant-scoped notifications with recipient-based notifications.
    const tenantQuery = query(
      collection(db, 'notifications'),
      where('tenantId', '==', user.uid)
    );
    const recipientQuery = query(
      collection(db, 'notifications'),
      where('recipientUserId', '==', user.uid)
    );

    let tenantDocs = [];
    let recipientDocs = [];

    const pushNotifications = () => {
      try {
        const notifs = mergeNotificationDocs(tenantDocs, recipientDocs).map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by createdAt (newest first) - handle both Timestamp and Date
        notifs.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() :
                       (a.createdAt?.getTime ? a.createdAt.getTime() :
                       (a.createdAt ? new Date(a.createdAt).getTime() : 0));
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() :
                       (b.createdAt?.getTime ? b.createdAt.getTime() :
                       (b.createdAt ? new Date(b.createdAt).getTime() : 0));
          return bTime - aTime;
        });

        setAllNotifications(notifs);

        setProperties(prevProperties => {
          if (prevProperties.length === 0) return prevProperties;

          return prevProperties.map(prop => {
            const unreadNotifCount = notifs.filter(
              n => {
                const matchesProperty = n.propertyId === prop.property?.id ||
                                      n.propertyId === prop.contract?.propertyId ||
                                      n.propertyId === prop.propertyId;
                return matchesProperty && isNotificationActionRequired(n);
              }
            ).length;
            return {
              ...prop,
              notificationCount: unreadNotifCount + (prop._unreadProblemCount || 0)
            };
          });
        });

        if (selectedPropertyId) {
          const propertyNotifications = notifs.filter(
            n => n.propertyId === selectedPropertyId ||
                 n.propertyId === contract?.propertyId ||
                 n.propertyId === property?.id
          );
          setNotifications(propertyNotifications.filter(isNotificationActionRequired));
        }
      } catch (error) {
        console.error('Error processing notifications:', error);
      }
    };

    const unsubTenant = onSnapshot(tenantQuery, (snapshot) => {
      tenantDocs = snapshot.docs;
      pushNotifications();
    }, (error) => {
      console.error('Error in tenant notification subscription:', error);
    });

    const unsubRecipient = onSnapshot(recipientQuery, (snapshot) => {
      recipientDocs = snapshot.docs;
      pushNotifications();
    }, (error) => {
      console.error('Error in recipient notification subscription:', error);
    });

    return () => {
      unsubTenant();
      unsubRecipient();
    };
  };

  const handleUpdatePaymentMethod = async (newMethod, bankDetails) => {
    try {
      // Update contract payment method
      await updateDoc(doc(db, 'contracts', contract.id), {
        paymentMethod: newMethod,
        bankAccount: bankDetails?.bankAccount || '',
        iban: bankDetails?.iban || '',
        updatedAt: serverTimestamp()
      });

      setContract({
        ...contract,
        paymentMethod: newMethod,
        bankAccount: bankDetails?.bankAccount || '',
        iban: bankDetails?.iban || ''
      });

      toast.success('Betaalmethode bijgewerkt');
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Error updating payment method:', error);
      toast.error('Fout bij bijwerken betaalmethode');
    }
  };

  const handleReportProblem = async (problemData) => {
    try {
      const landlordId = contract.landlordId || contract.landlordID || contract.core?.landlordID;

      await addDoc(collection(db, 'propertyReports'), {
        tenantId: user.uid,
        propertyId: property.id,
        contractId: contract.id,
        problemType: problemData.type,
        problemCategory: problemData.category,
        description: problemData.description,
        urgency: problemData.urgency || 'medium',
        additionalInfo: problemData.additionalInfo,
        status: 'pending',
        createdAt: serverTimestamp(),
        landlordId: landlordId || 'test-landlord-id'
      });

      if (landlordId) {
        try {
          const tenantName = user.displayName || user.name || user.email || 'De huurder';
          await addDoc(collection(db, 'notifications'), {
            type: 'property_problem_reported',
            recipientUserId: landlordId,
            tenantId: user.uid,
            propertyId: property.id,
            contractId: contract.id,
            title: 'Nieuw probleem gemeld',
            message: `${tenantName} heeft een probleem gemeld: ${problemData.category || problemData.type}`,
            read: false,
            createdAt: serverTimestamp(),
          });
        } catch (notifErr) {
          console.warn('Could not create problem notification:', notifErr);
        }
      }

      toast.success('Probleem gemeld! De verhuurder is op de hoogte gesteld.');
      setShowProblemModal(false);
    } catch (error) {
      console.error('Error reporting problem:', error);
      toast.error('Fout bij melden probleem');
    }
  };

  const handleMarkPaymentAsPaid = async () => {
    if (!user || !contract || !property) return;

    const landlordId = contract.landlordId || contract.landlordID || contract.core?.landlordID;
    if (!landlordId) {
      toast.error('Verhuurder niet gevonden');
      return;
    }

    const tenantName = user.displayName || user.name || user.email || 'De huurder';
    const paymentReference = contract.paymentReference || '';

    await addDoc(collection(db, 'notifications'), {
      type: 'payment_marked_paid',
      title: 'Huurder meldt betaling',
      message: `${tenantName} heeft gemeld dat de huurbetaling is uitgevoerd.${paymentReference ? ` Referentie: ${paymentReference}` : ''}`,
      recipientUserId: landlordId,
      tenantId: user.uid,
      propertyId: property.id || contract.propertyId || contract.kotID || contract.core?.kotID || '',
      contractId: contract.id,
      paymentReference,
      iban: contract.iban || '',
      read: false,
      requiresAction: true,
      status: 'pending_confirmation',
      createdAt: serverTimestamp()
    });
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId) || allNotifications.find(n => n.id === notificationId);
      if (!notification || notification.read) return;

      markNotificationHandledLocally(notification, 'opened');
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationAction = async (notificationId, action, customDate = null) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);
      if (!notification) return;

      if (action === 'accept') {
        // Accept the visit request with original or custom date
        await addDoc(collection(db, 'visitConfirmations'), {
          notificationId,
          tenantId: user.uid,
          propertyId: property.id,
          visitDate: customDate || notification.requestedDate,
          status: 'confirmed',
          createdAt: serverTimestamp()
        });

        // Mark notification as read
        await updateDoc(doc(db, 'notifications', notificationId), {
          read: true,
          action: 'accepted',
          actionDate: customDate || notification.requestedDate,
          readAt: serverTimestamp()
        });

        toast.success('Bezoekaanvraag geaccepteerd');
      } else if (action === 'decline') {
        await updateDoc(doc(db, 'notifications', notificationId), {
          read: true,
          action: 'declined',
          readAt: serverTimestamp()
        });
        toast.success('Bezoekaanvraag afgewezen');
      }

      markNotificationHandledLocally(notification, action);
      setShowNotificationDetail(null);
    } catch (error) {
      console.error('Error handling notification:', error);
      toast.error('Fout bij verwerken aanvraag');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ios-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Gegevens laden...</p>
        </div>
      </div>
    );
  }

  // Show property selection menu if multiple properties (or 1 active + archived)
  const showSelectionMenu = !selectedPropertyId && (properties.length > 1 || (properties.length >= 1 && archivedProperties.length > 0));
  if (showSelectionMenu) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container-professional py-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Mijn Panden</h1>
            <p className="text-gray-600">Selecteer een pand om te beheren</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((propData) => (
              <PropertySelectionCard
                key={propData.property.id}
                property={propData.property}
                contract={propData.contract}
                notificationCount={propData.notificationCount}
                onClick={() => setSelectedPropertyId(propData.property.id)}
              />
            ))}
          </div>

          <TenantArchivedSection archivedProperties={archivedProperties} />
        </div>
      </div>
    );
  }

  if (properties.length === 0 && archivedProperties.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Geen actief pand</h2>
          <p className="text-gray-600">
            Je hebt momenteel geen actief huurcontract. Zodra je een pand huurt, verschijnt het hier.
          </p>
        </div>
      </div>
    );
  }

  if (properties.length === 0 && archivedProperties.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container-professional py-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Mijn Pand</h1>
            <p className="text-gray-600">Je hebt momenteel geen actief huurcontract.</p>
          </div>
          <TenantArchivedSection archivedProperties={archivedProperties} />
        </div>
      </div>
    );
  }

  if (!property || !contract) {
    // If we have a routePropertyId but no property loaded, show loading
    if (routePropertyId && loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ios-blue mx-auto mb-4"></div>
            <p className="text-gray-600">Pand laden...</p>
          </div>
        </div>
      );
    }
    
    // If we have a routePropertyId but property not found, show error
    if (routePropertyId && !loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
          <div className="text-center max-w-md">
            <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Pand niet gevonden</h2>
            <p className="text-gray-600 mb-4">
              Het pand dat je zoekt kon niet worden gevonden. Mogelijk is het contract nog niet volledig opgeslagen.
            </p>
            <button
              onClick={() => navigate('/account')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Terug naar Account
            </button>
          </div>
        </div>
      );
    }
    
    return null; // Loading state
  }

  const pendingNotifications = notifications.filter(isNotificationActionRequired);
  const terminationNotifCount = notifications.filter(n =>
    !n.read && (n.type || '').startsWith('termination_')
  ).length;
  const overviewBadge = terminationNotifCount + (activeTermination ? 1 : 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="container-professional py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              {properties.length > 1 && (
                <button
                  onClick={() => setSelectedPropertyId(null)}
                  className="flex items-center space-x-2 text-gray-600 hover:text-ios-blue mb-2 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="font-semibold">Terug naar overzicht</span>
                </button>
              )}
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Mijn Pand</h1>
              <p className="text-gray-600">Beheer je huurpand, contract en communicatie</p>
            </div>
            {pendingNotifications.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setActiveTab('notifications')}
                  className="relative p-3 bg-ios-blue text-white rounded-xl hover:bg-blue-600 transition-all shadow-lg"
                >
                  <Bell className="w-6 h-6" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {pendingNotifications.length}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center space-x-2 bg-white rounded-xl shadow-lg border border-gray-200 p-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all relative ${
                activeTab === 'overview'
                  ? 'bg-ios-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Overzicht
              {overviewBadge > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {overviewBadge > 9 ? '9+' : overviewBadge}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('contract')}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'contract'
                  ? 'bg-ios-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Contract
            </button>
            <button
              onClick={() => setActiveTab('payment')}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'payment'
                  ? 'bg-ios-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Betaling
            </button>
            <button
              onClick={() => setActiveTab('problems')}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all relative ${
                activeTab === 'problems'
                  ? 'bg-ios-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Problemen
              {myProblems.filter(p => p.status === 'responded' && !p.readByTenant).length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {myProblems.filter(p => p.status === 'responded' && !p.readByTenant).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all relative ${
                activeTab === 'notifications'
                  ? 'bg-ios-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Meldingen
              {pendingNotifications.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingNotifications.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab 
            property={property} 
            contract={contract}
            onReportProblem={() => setShowProblemModal(true)}
            activeTermination={activeTermination}
          />
        )}

        {activeTab === 'contract' && (
          <ContractTab
            contract={contract}
            property={property}
            archivedContracts={archivedContractsForProperty}
          />
        )}

        {activeTab === 'payment' && (
          <PaymentTab 
            contract={contract}
            onUpdatePayment={() => setShowPaymentModal(true)}
            onMarkPaid={handleMarkPaymentAsPaid}
          />
        )}

        {activeTab === 'problems' && (
          <TenantProblemsTab
            problems={myProblems}
            onReportNew={() => setShowProblemModal(true)}
          />
        )}

        {activeTab === 'notifications' && (
          <NotificationsTab 
            notifications={pendingNotifications}
            contract={contract}
            navigate={navigate}
            onNotificationClick={(notif) => {
              if (isNotificationPending(notif) && notif.type !== 'visit_request') {
                markNotificationAsRead(notif.id);
              }
              setShowNotificationDetail(notif);
            }}
          />
        )}

        {/* Problem Report Modal */}
        {showProblemModal && (
          <ProblemReportModal
            property={property}
            onClose={() => setShowProblemModal(false)}
            onSubmit={handleReportProblem}
          />
        )}

        {/* Payment Method Modal */}
        {showPaymentModal && (
          <PaymentMethodModal
            currentMethod={contract.paymentMethod}
            bankAccount={contract.bankAccount}
            iban={contract.iban}
            onClose={() => setShowPaymentModal(false)}
            onSave={handleUpdatePaymentMethod}
          />
        )}

        {/* Notification Detail Modal */}
        {showNotificationDetail && (
          <NotificationDetailModal
            notification={showNotificationDetail}
            onClose={() => setShowNotificationDetail(null)}
            onAction={handleNotificationAction}
          />
        )}
      </div>
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ property, contract, onReportProblem, activeTermination }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  
  const handleContactLandlord = async () => {
    if (!user || !contract.landlordId) {
      toast.error('Verhuurder informatie niet beschikbaar');
      return;
    }
    
    try {
      setIsOpeningChat(true);
      // Get or create chat between tenant and landlord
      const chat = await createOrGetChat(user.uid, contract.landlordId, property.id);
      // Navigate directly to the chat
      navigate(`/messages?threadId=${chat.id}`);
    } catch (error) {
      console.error('Error opening chat:', error);
      toast.error('Kon chat niet openen: ' + (error.message || 'Onbekende fout'));
      setIsOpeningChat(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Property Card */}
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          <div className="relative h-64 md:h-full min-h-[300px]">
            <PropertyImage
              property={property}
              className="w-full h-full object-cover"
              width={600}
              height={400}
            />
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
              <span className={`text-sm font-semibold ${getContractDisplayStatus(contract).color}`}>
                {getContractDisplayStatus(contract).label}
              </span>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{property.titel}</h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-center text-gray-600">
                <MapPin className="w-5 h-5 mr-3 text-ios-blue" />
                <span>{property.adres}, {property.postcode} {property.stad}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Euro className="w-5 h-5 mr-3 text-ios-blue" />
                <span className="font-semibold text-gray-900">€{contract.monthlyRent}/maand</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Home className="w-5 h-5 mr-3 text-ios-blue" />
                <span>{property.oppervlakte}m² - {property.type}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Verhuurder</h3>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-ios-blue to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {contract.landlordName?.[0] || 'V'}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{contract.landlordName}</p>
                  <p className="text-sm text-gray-600">{contract.landlordEmail}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(contract.keyHandoverInstructions || contract.core?.keyHandoverInstructions) && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-orange-200 p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Sleuteloverdracht instructies</h3>
          </div>
          <p className="text-gray-700 whitespace-pre-wrap">
            {contract.keyHandoverInstructions || contract.core?.keyHandoverInstructions}
          </p>
        </div>
      )}

      {/* Termination Request Alert */}
      {activeTermination && (
        <TenantTerminationAlert termination={activeTermination} contractId={contract?.id} />
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={onReportProblem}
          className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
        >
          <AlertTriangle className="w-8 h-8 mb-3" />
          <h3 className="font-bold text-lg mb-1">Probleem Melden</h3>
          <p className="text-sm text-red-100">Rapporteer een probleem met je pand</p>
        </button>

        <button
          onClick={handleContactLandlord}
          disabled={isOpeningChat}
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          <MessageCircle className={`w-8 h-8 mb-3 ${isOpeningChat ? 'animate-pulse' : ''}`} />
          <h3 className="font-bold text-lg mb-1">Contact Verhuurder</h3>
          <p className="text-sm text-blue-100">
            {isOpeningChat ? 'Chat openen...' : 'Stuur een bericht'}
          </p>
        </button>

        <button
          onClick={() => navigate(`/contract/${contract.id}`)}
          className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
        >
          <FileText className="w-8 h-8 mb-3" />
          <h3 className="font-bold text-lg mb-1">Bekijk Contract</h3>
          <p className="text-sm text-purple-100">Volledige contractdetails</p>
        </button>
      </div>
    </div>
  );
}

// Contract Tab Component
function ContractTab({ contract, property, archivedContracts = [] }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showTerminationForm, setShowTerminationForm] = useState(false);
  const [termReasonCode, setTermReasonCode] = useState('');
  const [termReasonText, setTermReasonText] = useState('');
  const [termEndDate, setTermEndDate] = useState('');
  const [termSubmitting, setTermSubmitting] = useState(false);
  const [existingRequests, setExistingRequests] = useState([]);

  useEffect(() => {
    if (!contract?.id || !user?.uid) return;
    const trCol = collection(db, 'terminationRequests');
    Promise.all([
      getDocs(query(trCol, where('contractId', '==', contract.id), where('initiatorUserId', '==', user.uid))),
      getDocs(query(trCol, where('contractId', '==', contract.id), where('counterpartyUserId', '==', user.uid)))
    ]).then(([s1, s2]) => {
      const seen = new Set();
      const all = [];
      for (const snap of [s1, s2]) {
        for (const d of snap.docs) {
          if (!seen.has(d.id)) { seen.add(d.id); all.push({ id: d.id, ...d.data() }); }
        }
      }
      setExistingRequests(all);
    }).catch(() => {});
  }, [contract?.id, user?.uid]);

  const activeRequest = existingRequests.find(r =>
    ['requested', 'counter_proposed', 'accepted_by_tenant', 'accepted_by_landlord', 'mutual_acceptance_reached'].includes(r.status)
  );

  const handleTerminationSubmit = async () => {
    if (!termReasonCode || !termEndDate) {
      toast.error('Selecteer een reden en einddatum');
      return;
    }
    setTermSubmitting(true);
    try {
      await createTerminationRequest({
        contractId: contract.id,
        listingId: contract.kotID || contract.core?.kotID || contract.propertyId || null,
        initiatorRole: 'tenant',
        country: 'BE',
        contractType: 'student',
        reasonCode: termReasonCode,
        reasonText: termReasonText || null,
        requestedEndDate: termEndDate,
        attachments: []
      });
      toast.success('Opzegging ingediend!');
      setShowTerminationForm(false);
      const trCol = collection(db, 'terminationRequests');
      const [s1, s2] = await Promise.all([
        getDocs(query(trCol, where('contractId', '==', contract.id), where('initiatorUserId', '==', user.uid))),
        getDocs(query(trCol, where('contractId', '==', contract.id), where('counterpartyUserId', '==', user.uid)))
      ]);
      const seen = new Set();
      const refreshed = [];
      for (const snap of [s1, s2]) {
        for (const d of snap.docs) {
          if (!seen.has(d.id)) { seen.add(d.id); refreshed.push({ id: d.id, ...d.data() }); }
        }
      }
      setExistingRequests(refreshed);
    } catch (err) {
      toast.error(err.message || 'Fout bij het indienen van de opzegging');
    } finally {
      setTermSubmitting(false);
    }
  };

  const legalOptions = [
    {
      title: 'Contract Opzeggen',
      description: 'Je contract opzeggen volgens de geldende opzegtermijn',
      icon: FileText,
      color: 'text-orange-600 bg-orange-100',
      action: () => {
        if (activeRequest) {
          toast('Er is al een actief opzegverzoek');
        } else {
          setShowTerminationForm(true);
        }
      }
    },
    {
      title: 'Herziening Vragen',
      description: 'Vraag een herziening van je huurprijs',
      icon: Euro,
      color: 'text-blue-600 bg-blue-100',
      action: () => toast('Contacteer je verhuurder voor huurprijsherziening')
    },
    {
      title: 'Klacht Indienen',
      description: 'Dien een formele klacht in over je huurcontract',
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      action: () => toast('Klacht indienen via je huurdersorganisatie')
    },
    {
      title: 'Rechtelijke Bijstand',
      description: 'Neem contact op met een advocaat',
      icon: Shield,
      color: 'text-purple-600 bg-purple-100',
      action: () => toast('Contacteer een huurrechtadvocaat')
    }
  ];

  return (
    <div className="space-y-6">
      {/* Contract Info Card */}
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Huurcontract</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Contractnummer</label>
            <p className="text-lg font-semibold text-gray-900">{contract.id}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Status</label>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getContractDisplayStatus(contract).bg}`}>
              <CheckCircle className="w-4 h-4 mr-1" />
              {getContractDisplayStatus(contract).label}
            </span>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Maandelijkse Huur</label>
            <p className="text-lg font-semibold text-gray-900">€{contract.monthlyRent}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Waarborg</label>
            <p className="text-lg font-semibold text-gray-900">€{contract.deposit}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Startdatum</label>
            <p className="text-lg font-semibold text-gray-900">
              {contract.startDate.toLocaleDateString('nl-NL')}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Einddatum</label>
            <p className="text-lg font-semibold text-gray-900">
              {contract.endDate.toLocaleDateString('nl-NL')}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Contracttype</label>
            <p className="text-lg font-semibold text-gray-900">
              {contract.contractType === 'fixed' ? 'Bepaalde tijd' : 'Onbepaalde tijd'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Betaalmethode</label>
            <p className="text-lg font-semibold text-gray-900">
              {contract.paymentMethod === 'bank_transfer' ? 'Bankoverschrijving' : 
               contract.paymentMethod === 'direct_debit' ? 'Automatische incasso' : 'Contant'}
            </p>
          </div>
        </div>
      </div>

      {/* Legal Options */}
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Rechtelijke Opties</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {legalOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <button
                key={index}
                onClick={option.action}
                className="text-left p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all border-2 border-transparent hover:border-ios-blue"
              >
                <div className={`w-12 h-12 ${option.color} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{option.title}</h3>
                <p className="text-sm text-gray-600">{option.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Termination Request Banner */}
      {activeRequest && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-2">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            <h3 className="text-lg font-bold text-orange-900">Opzegverzoek actief</h3>
          </div>
          <p className="text-sm text-orange-700">
            {TERMINATION_STATUS_LABELS[activeRequest.status] || activeRequest.status}
          </p>
        </div>
      )}

      {/* Inline Termination Form */}
      {/* Archived Contract Versions */}
      {archivedContracts.length > 0 && (
        <div className="bg-gray-50 rounded-2xl shadow border-2 border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center">
              <Archive className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-700">Vorige contractversies</h3>
              <p className="text-sm text-gray-500">{archivedContracts.length} gearchiveerd{archivedContracts.length > 1 ? 'e versies' : 'e versie'}</p>
            </div>
          </div>
          <div className="space-y-3">
            {archivedContracts
              .sort((a, b) => {
                const da = a.archivedAt?.toDate ? a.archivedAt.toDate() : (a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0));
                const db2 = b.archivedAt?.toDate ? b.archivedAt.toDate() : (b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0));
                return db2 - da;
              })
              .map((ac) => {
                const archivedDate = ac.archivedAt?.toDate ? ac.archivedAt.toDate().toLocaleDateString('nl-NL') : null;
                const createdDate = ac.createdAt?.toDate ? ac.createdAt.toDate().toLocaleDateString('nl-NL') : null;
                const rent = ac.core?.rent || ac.monthlyRent || ac.rent || '—';
                return (
                  <div key={ac.id} className="bg-white rounded-xl border border-gray-200 p-4 opacity-75">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
                          Gearchiveerd
                        </span>
                        {ac.isEditedVersion && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            Gewijzigde versie
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => navigate(`/contract/${ac.id}`)}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        Bekijken <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Huurprijs:</span>{' '}
                        <span className="font-semibold text-gray-700">€{rent}/maand</span>
                      </div>
                      {createdDate && (
                        <div>
                          <span className="text-gray-500">Aangemaakt:</span>{' '}
                          <span className="font-semibold text-gray-700">{createdDate}</span>
                        </div>
                      )}
                      {archivedDate && (
                        <div>
                          <span className="text-gray-500">Gearchiveerd:</span>{' '}
                          <span className="font-semibold text-gray-700">{archivedDate}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {showTerminationForm && !activeRequest && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-orange-200 p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Contract Opzeggen</h2>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reden *</label>
              <select
                value={termReasonCode}
                onChange={e => setTermReasonCode(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-orange-400 focus:ring-0"
              >
                <option value="">Selecteer een reden</option>
                <option value="end_of_term">Einde contractperiode</option>
                <option value="early_termination">Vroegtijdige beëindiging</option>
                <option value="study_stopped">Studie stopgezet</option>
                <option value="financial_issue">Financiële redenen</option>
                <option value="conflict">Conflict</option>
                <option value="other">Andere reden</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gewenste einddatum *</label>
              <input
                type="date"
                value={termEndDate}
                onChange={e => setTermEndDate(e.target.value)}
                min={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-orange-400 focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Toelichting (optioneel)</label>
              <textarea
                value={termReasonText}
                onChange={e => setTermReasonText(e.target.value)}
                rows={3}
                placeholder="Licht je reden toe..."
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-orange-400 focus:ring-0 resize-none"
              />
            </div>

            <div className="flex space-x-3 pt-2">
              <button
                onClick={() => setShowTerminationForm(false)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
              >
                Annuleren
              </button>
              <button
                onClick={handleTerminationSubmit}
                disabled={termSubmitting || !termReasonCode || !termEndDate}
                className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {termSubmitting ? 'Indienen...' : 'Opzegging indienen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Payment Tab Component
function PaymentTab({ contract, onUpdatePayment, onMarkPaid }) {
  const [isSubmittingPaid, setIsSubmittingPaid] = useState(false);
  const [paymentMarked, setPaymentMarked] = useState(false);

  const handlePaymentMarked = async () => {
    if (!onMarkPaid || isSubmittingPaid) return;
    try {
      setIsSubmittingPaid(true);
      await onMarkPaid();
      setPaymentMarked(true);
      toast.success('Melding verzonden naar de verhuurder');
    } catch (error) {
      console.error('Error sending payment confirmation notification:', error);
      toast.error('Kon melding niet verzenden');
    } finally {
      setIsSubmittingPaid(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
              <Euro className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Betaalgegevens</h2>
          </div>
          <button
            onClick={onUpdatePayment}
            className="flex items-center space-x-2 px-4 py-2 bg-ios-blue text-white rounded-lg hover:bg-blue-600 transition-all"
          >
            <Edit3 className="w-4 h-4" />
            <span>Wijzigen</span>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">Betaalmethode</label>
            <p className="text-lg font-semibold text-gray-900">
              {contract.paymentMethod === 'bank_transfer' ? 'Bankoverschrijving' : 
               contract.paymentMethod === 'direct_debit' ? 'Automatische incasso' : 'Contant'}
            </p>
          </div>

          <div className="w-full text-left border border-green-200 bg-green-50 rounded-xl p-4">
            <div>
              <label className="text-sm font-medium text-green-700 mb-1 block">Maandelijkse Huur</label>
              <p className="text-2xl font-bold text-green-700">€{contract.monthlyRent}</p>
            </div>
            <div className="mt-4 border border-green-200 rounded-xl p-4 space-y-3 bg-white">
              {contract.iban && (
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-1 block">IBAN</label>
                  <p className="text-lg font-semibold text-gray-900">{contract.iban}</p>
                </div>
              )}
              {contract.paymentReference && (
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-1 block">Betaalreferentie</label>
                  <p className="text-lg font-semibold text-gray-900">{contract.paymentReference}</p>
                </div>
              )}
              <button
                type="button"
                onClick={handlePaymentMarked}
                disabled={isSubmittingPaid || paymentMarked}
                className="w-full mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {paymentMarked ? 'Gemeld aan verhuurder' : isSubmittingPaid ? 'Bezig...' : 'Ik heb betaald'}
              </button>
            </div>
          </div>

          <div className="w-full text-left border border-blue-200 bg-blue-50 rounded-xl p-4">
            <div>
              <label className="text-sm font-medium text-blue-700 mb-1 block">Waarborg</label>
              <p className="text-2xl font-bold text-blue-700">€{contract.deposit}</p>
            </div>
            <div className="mt-4 border border-blue-200 rounded-xl p-4 bg-white space-y-3">
              {contract.depositPaymentReference && (
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-1 block">Waarborg referentie</label>
                  <p className="text-base text-gray-900">{contract.depositPaymentReference}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">Waarborg instructies</label>
                <p className="text-base text-gray-900 whitespace-pre-wrap">
                  {contract.depositPaymentInstructions || 'Geen waarborg instructies toegevoegd.'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">Sleuteloverdracht instructies</label>
                <p className="text-base text-gray-900 whitespace-pre-wrap">
                  {contract.keyHandoverInstructions || 'Geen sleuteloverdracht instructies toegevoegd.'}
                </p>
              </div>
            </div>
          </div>

          {Number(contract.voorschotAmount || 0) > 0 && (
            <div className="w-full text-left border border-orange-200 bg-orange-50 rounded-xl p-4">
              <div>
                <label className="text-sm font-medium text-orange-700 mb-1 block">Voorschot (eenmalig)</label>
                <p className="text-2xl font-bold text-orange-700">€{contract.voorschotAmount}</p>
              </div>
              <div className="mt-4 border border-orange-200 rounded-xl p-4 bg-white">
                <label className="text-sm font-medium text-gray-600 mb-1 block">Voorschot instructies</label>
                <p className="text-base text-gray-900 whitespace-pre-wrap">
                  {contract.voorschotPaymentInstructions || 'Geen voorschot instructies toegevoegd.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Property Selection Card Component
function PropertySelectionCard({ property, contract, notificationCount, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group bg-white rounded-2xl shadow-lg border-2 border-gray-200 hover:border-ios-blue overflow-hidden transition-all duration-300 transform hover:-translate-y-2 text-left"
    >
      <div className="relative h-48">
        <PropertyImage
          property={property}
          className="w-full h-full object-cover"
          width={400}
          height={300}
        />
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
          <span className={`text-sm font-semibold ${getContractDisplayStatus(contract).color}`}>
            {getContractDisplayStatus(contract).label}
          </span>
        </div>
        {notificationCount > 0 && (
          <div className="absolute top-4 left-4 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shadow-lg">
            {notificationCount}
          </div>
        )}
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-ios-blue transition-colors">
          {property.titel}
        </h3>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-gray-600 text-sm">
            <MapPin className="w-4 h-4 mr-2 text-ios-blue" />
            <span>{property.adres}, {property.postcode} {property.stad}</span>
          </div>
          <div className="flex items-center text-gray-600 text-sm">
            <Euro className="w-4 h-4 mr-2 text-ios-blue" />
            <span className="font-semibold text-gray-900">€{contract.monthlyRent}/maand</span>
          </div>
          <div className="flex items-center text-gray-600 text-sm">
            <Home className="w-4 h-4 mr-2 text-ios-blue" />
            <span>{property.oppervlakte}m² - {property.type}</span>
          </div>
        </div>

        {notificationCount > 0 && (
          <div className="flex items-center space-x-2 text-sm font-semibold text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            <Bell className="w-4 h-4" />
            <span>{notificationCount} nieuwe melding{notificationCount !== 1 ? 'en' : ''}</span>
          </div>
        )}

        <div className="flex items-center text-ios-blue font-semibold text-sm mt-4">
          <span>Bekijk details</span>
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </button>
  );
}

// Notifications Tab Component
function NotificationsTab({ notifications, contract, onNotificationClick, navigate }) {
  const keyHandoverInstructions = contract?.keyHandoverInstructions || contract?.core?.keyHandoverInstructions || '';
  const amendmentNotifs = notifications.filter(n => (n.type || '').includes('amendment'));
  const otherNotifs = notifications.filter(n => !(n.type || '').includes('amendment'));

  if (notifications.length === 0 && !keyHandoverInstructions) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-green-50 p-10 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg">
          <CheckCircle className="h-8 w-8" />
        </div>
        <h3 className="mb-2 text-xl font-bold text-gray-900">Geen open acties</h3>
        <p className="text-gray-600">Alles is afgehandeld. Nieuwe meldingen verschijnen hier automatisch.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {keyHandoverInstructions && (
        <div className="group relative rounded-2xl border border-orange-200 bg-orange-50/70 p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-amber-500">
                  <Building className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-bold text-gray-900">Sleuteloverdracht instructies</h3>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{keyHandoverInstructions}</p>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow">
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Action required</h3>
              <p className="text-xs text-gray-600">Alleen open taken die nog jouw actie vragen</p>
            </div>
          </div>
          <span className="inline-flex min-w-9 items-center justify-center rounded-full border border-amber-200 bg-white px-3 py-1 text-sm font-bold text-amber-700 shadow-sm">
            {notifications.length + (keyHandoverInstructions ? 1 : 0)}
          </span>
        </div>
      </div>

      {amendmentNotifs.map((notif) => (
        <div
          key={notif.id}
          className="group relative rounded-2xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 via-white to-blue-50 p-5 shadow-sm"
        >
          <div className="absolute right-4 top-4 h-2.5 w-2.5 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 animate-pulse"></div>
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 shadow-lg">
              <PenTool className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 mb-1">{notif.title || 'Contractwijziging'}</h3>
              <p className="text-gray-600 text-sm mb-3">{notif.message || notif.description}</p>
              <button
                onClick={() => {
                  if (notif.actionUrl) {
                    navigate(notif.actionUrl);
                  } else if (notif.contractId && notif.amendmentId) {
                    navigate(`/contract/${notif.contractId}/amendment/${notif.amendmentId}`);
                  } else {
                    navigate('/contracten');
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
              >
                <FileText className="w-4 h-4" />
                Bekijk in Contracten
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {otherNotifs.map((notif) => (
        <div
          key={notif.id}
          onClick={() => onNotificationClick(notif)}
          className="group relative cursor-pointer rounded-2xl border border-amber-100 bg-white/95 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md"
        >
          <div className="absolute right-4 top-4 h-2.5 w-2.5 rounded-full bg-gradient-to-br from-amber-500 to-rose-500"></div>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-ios-blue to-blue-600">
                  {notif.type === 'visit_request' ? (
                    <Calendar className="h-5 w-5 text-white" />
                  ) : (
                    <Bell className="h-5 w-5 text-white" />
                  )}
                </div>
                <h3 className="font-bold text-gray-900">{notif.title || 'Nieuwe melding'}</h3>
              </div>
              <p className="text-gray-600 mb-2">{notif.message || notif.description}</p>
              {notif.requestedDate && (
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="mr-1 h-4 w-4" />
                  <span>Gevraagde datum: {notif.requestedDate.toDate ? notif.requestedDate.toDate().toLocaleDateString('nl-NL') : new Date(notif.requestedDate).toLocaleDateString('nl-NL')}</span>
                </div>
              )}
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Tenant Problems Tab — shows reported problems and landlord responses
function TenantProblemsTab({ problems, onReportNew }) {
  const statusLabels = {
    pending: { label: 'In behandeling', color: 'bg-amber-100 text-amber-700' },
    responded: { label: 'Beantwoord', color: 'bg-green-100 text-green-700' },
    resolved: { label: 'Opgelost', color: 'bg-blue-100 text-blue-700' },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Mijn Problemen</h2>
        <button
          onClick={onReportNew}
          className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors shadow-sm"
        >
          <AlertTriangle className="w-4 h-4" />
          Nieuw probleem
        </button>
      </div>

      {problems.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Geen gemelde problemen</h3>
          <p className="text-gray-500">Als je een probleem hebt met je pand kun je het hier melden.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {problems.map((problem) => {
            const st = statusLabels[problem.status] || statusLabels.pending;
            const isNewResponse = problem.status === 'responded' && !problem.readByTenant;
            return (
              <div
                key={problem.id}
                className={`bg-white rounded-2xl shadow-lg border p-6 ${isNewResponse ? 'border-green-300 ring-2 ring-green-100' : 'border-gray-200'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900">{problem.problemCategory || problem.problemType || 'Probleem'}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span>
                      {isNewResponse && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-500 text-white animate-pulse">Nieuw antwoord</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      Gemeld op {problem.createdAt instanceof Date ? problem.createdAt.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                    </p>
                  </div>
                  {problem.urgency && (
                    <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                      problem.urgency === 'urgent' ? 'bg-red-100 text-red-700' :
                      problem.urgency === 'high' ? 'bg-orange-100 text-orange-700' :
                      problem.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {problem.urgency === 'urgent' ? 'Spoed' :
                       problem.urgency === 'high' ? 'Hoog' :
                       problem.urgency === 'medium' ? 'Normaal' : 'Laag'}
                    </span>
                  )}
                </div>

                <div className="bg-gray-50 rounded-xl p-4 mb-3">
                  <p className="text-sm font-medium text-gray-500 mb-1">Jouw beschrijving:</p>
                  <p className="text-gray-800 whitespace-pre-line">{problem.description}</p>
                </div>

                {problem.landlordResponse && (
                  <div className={`rounded-xl p-4 border ${isNewResponse ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                    <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <MessageCircle className="w-4 h-4" />
                      Antwoord verhuurder:
                    </p>
                    <p className="text-gray-900 whitespace-pre-line">{problem.landlordResponse}</p>
                    {problem.respondedAt && (
                      <p className="text-xs text-gray-400 mt-2">
                        Beantwoord op {problem.respondedAt instanceof Date ? problem.respondedAt.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    )}
                    
                  </div>
                )}

                {problem.status === 'pending' && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-amber-600">
                    <Clock className="w-4 h-4" />
                    <span>Wachten op reactie van verhuurder...</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Problem Report Modal
function ProblemReportModal({ property, onClose, onSubmit }) {
  const [problemType, setProblemType] = useState(null);
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [additionalInfo, setAdditionalInfo] = useState('');

  const commonProblems = [
    { type: 'leak', label: 'Lekkage', icon: Droplet, category: 'Water' },
    { type: 'heating', label: 'Verwarming', icon: Zap, category: 'Verwarming' },
    { type: 'electrical', label: 'Elektriciteit', icon: Zap, category: 'Elektriciteit' },
    { type: 'plumbing', label: 'Sanitair', icon: Wrench, category: 'Sanitair' },
    { type: 'security', label: 'Beveiliging', icon: Shield, category: 'Beveiliging' },
    { type: 'other', label: 'Anders', icon: AlertTriangle, category: 'Overig' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!problemType || !description.trim()) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    const selectedProblem = commonProblems.find(p => p.type === problemType);
    onSubmit({
      type: problemType,
      category: selectedProblem?.category || 'Overig',
      description,
      urgency,
      additionalInfo
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Probleem Melden</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Selecteer probleemtype *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {commonProblems.map((problem) => {
                const Icon = problem.icon;
                return (
                  <button
                    key={problem.type}
                    type="button"
                    onClick={() => setProblemType(problem.type)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      problemType === problem.type
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-2 ${
                      problemType === problem.type ? 'text-red-600' : 'text-gray-400'
                    }`} />
                    <p className="text-sm font-medium text-gray-900">{problem.label}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Beschrijving van het probleem *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Beschrijf het probleem zo gedetailleerd mogelijk..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Urgentie
            </label>
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="low">Laag - Kan wachten</option>
              <option value="medium">Gemiddeld - Binnenkort oplossen</option>
              <option value="high">Hoog - Dringend</option>
              <option value="urgent">Urgent - Direct actie vereist</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Extra informatie (optioneel)
            </label>
            <textarea
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Extra details, foto's beschrijving, etc."
            />
          </div>

          <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Annuleren
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Probleem Melden
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Payment Method Modal
function PaymentMethodModal({ currentMethod, bankAccount, iban, onClose, onSave }) {
  const [paymentMethod, setPaymentMethod] = useState(currentMethod || 'bank_transfer');
  const [bankDetails, setBankDetails] = useState({
    bankAccount: bankAccount || '',
    iban: iban || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(paymentMethod, bankDetails);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Betaalmethode Wijzigen</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Betaalmethode
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="bank_transfer">Bankoverschrijving</option>
              <option value="direct_debit">Automatische incasso</option>
            </select>
          </div>

          {(paymentMethod === 'bank_transfer' || paymentMethod === 'direct_debit') && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rekeningnummer
                </label>
                <input
                  type="text"
                  value={bankDetails.bankAccount}
                  onChange={(e) => setBankDetails({ ...bankDetails, bankAccount: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="BE12 3456 7890 1234"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  IBAN
                </label>
                <input
                  type="text"
                  value={bankDetails.iban}
                  onChange={(e) => setBankDetails({ ...bankDetails, iban: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="BE12345678901234"
                />
              </div>
            </>
          )}

          <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Annuleren
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Opslaan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Notification Detail Modal
function NotificationDetailModal({ notification, onClose, onAction }) {
  const [proposedDate, setProposedDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const requestedDate = notification.requestedDate?.toDate ? notification.requestedDate.toDate() : new Date(notification.requestedDate);

  const handleAccept = () => {
    if (proposedDate) {
      onAction(notification.id, 'accept', new Date(proposedDate));
    } else {
      onAction(notification.id, 'accept');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        <div className="bg-gradient-to-r from-ios-blue to-blue-600 text-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">{notification.title || 'Melding'}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <p className="text-gray-700">{notification.message || notification.description}</p>
          </div>

          {notification.type === 'visit_request' && (
            <>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Gevraagde bezoekdatum</h3>
                </div>
                <p className="text-gray-700">
                  {requestedDate.toLocaleDateString('nl-NL', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>

              <div>
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all flex items-center justify-between"
                >
                  <span>Andere datum voorstellen</span>
                  {showDatePicker ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                {showDatePicker && (
                  <div className="mt-3">
                    <input
                      type="date"
                      value={proposedDate}
                      onChange={(e) => setProposedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
            {notification.type === 'visit_request' && (
              <>
                <button
                  onClick={() => onAction(notification.id, 'decline')}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                >
                  Afwijzen
                </button>
                <button
                  onClick={handleAccept}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  {proposedDate ? 'Accepteer met nieuwe datum' : 'Accepteer'}
                </button>
              </>
            )}
            {notification.type !== 'visit_request' && (
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-ios-blue text-white rounded-xl font-semibold hover:bg-blue-600 transition-all"
              >
                Sluiten
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TenantTerminationAlert({ termination, contractId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [actionMode, setActionMode] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [counterDate, setCounterDate] = useState('');
  const [counterReason, setCounterReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [actionCompleted, setActionCompleted] = useState(null);

  const tr = termination;

  useEffect(() => {
    if (tr && user?.uid && tr.counterpartyUserId === user.uid && !tr.delivery?.inAppViewedAt) {
      markTerminationViewed(tr.id).catch(() => {});
    }
  }, [tr?.id, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tr) return null;

  const resolvedStatus = actionCompleted || tr.status;
  const isTenantInitiator = user?.uid === tr.initiatorUserId && tr.initiatorRole === 'tenant';
  const isCounterparty = user?.uid === tr.counterpartyUserId;
  const needsResponse = !actionCompleted
    && ((isCounterparty && ['requested', 'counter_proposed', 'accepted_by_landlord'].includes(tr.status))
    || (isTenantInitiator && tr.status === 'counter_proposed'));

  const allReasons = [...(TERMINATION_REASON_OPTIONS.tenant || []), ...(TERMINATION_REASON_OPTIONS.landlord || [])];
  const reasonLabel = allReasons.find(o => o.value === tr.reasonCode)?.label || tr.reasonCode || 'Onbekend';

  const handleAction = async (action) => {
    setProcessing(true);
    try {
      const payload = { requestId: tr.id, action };
      if (action === 'reject') {
        if (!rejectReason.trim() || rejectReason.trim().length < 3) {
          toast.error('Geef een reden op (minimaal 3 tekens)');
          setProcessing(false);
          return;
        }
        payload.reasonText = rejectReason.trim();
      }
      if (action === 'counter_propose') {
        if (!counterDate) {
          toast.error('Kies een datum');
          setProcessing(false);
          return;
        }
        payload.counterProposedEndDate = counterDate;
        if (counterReason.trim()) payload.reasonText = counterReason.trim();
      }
      await updateTerminationRequest(payload);
      toast.success(
        action === 'accept' ? 'Akkoord gegeven' :
        action === 'reject' ? 'Afgewezen' :
        'Tegenvoorstel verzonden'
      );
      setActionMode(null);
      if (action === 'accept') setActionCompleted('mutual_acceptance_reached');
      if (action === 'reject') setActionCompleted('rejected');
    } catch (error) {
      toast.error(error.message || 'Er is een fout opgetreden');
    } finally {
      setProcessing(false);
    }
  };

  let bannerTitle, bannerDesc, bannerColor;
  if (resolvedStatus === 'mutual_acceptance_reached' || resolvedStatus === 'completed') {
    const endDateStr = formatTerminationDate(tr.counterProposedEndDate || tr.requestedEndDate);
    bannerTitle = 'Opzegging akkoord';
    bannerDesc = `Beide partijen zijn akkoord. Het contract loopt af op ${endDateStr}.`;
    bannerColor = 'border-green-300 bg-green-50';
  } else if (resolvedStatus === 'rejected') {
    bannerTitle = 'Opzegging afgewezen';
    bannerDesc = 'Het opzegverzoek is afgewezen.';
    bannerColor = 'border-red-300 bg-red-50';
  } else if (isTenantInitiator && resolvedStatus === 'requested') {
    bannerTitle = 'Opzegverzoek ingediend';
    bannerDesc = 'Je opzegverzoek is verstuurd. Wachten op reactie van de verhuurder.';
    bannerColor = 'border-blue-300 bg-blue-50';
  } else if (resolvedStatus === 'counter_proposed') {
    bannerTitle = 'Tegenvoorstel van verhuurder';
    bannerDesc = `Nieuwe voorgestelde einddatum: ${formatTerminationDate(tr.counterProposedEndDate)}`;
    bannerColor = 'border-amber-300 bg-amber-50';
  } else if (isCounterparty && resolvedStatus === 'requested') {
    bannerTitle = 'Opzegverzoek van verhuurder';
    bannerDesc = 'Je verhuurder heeft een opzegverzoek ingediend.';
    bannerColor = 'border-orange-300 bg-orange-50';
  } else {
    bannerTitle = 'Opzegverzoek';
    bannerDesc = TERMINATION_STATUS_LABELS[resolvedStatus] || resolvedStatus;
    bannerColor = 'border-gray-300 bg-gray-50';
  }

  return (
    <div className={`rounded-2xl border-2 ${bannerColor} p-5 space-y-4`}>
      <div className="flex items-center space-x-3">
        <ShieldAlert className="w-6 h-6 text-orange-600 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900">{bannerTitle}</h3>
          <p className="text-sm text-gray-700">{bannerDesc}</p>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${TERMINATION_STATUS_STYLES[tr.status] || 'bg-gray-100 text-gray-700'}`}>
          {TERMINATION_STATUS_LABELS[tr.status] || tr.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-gray-500">Reden</span><p className="font-medium text-gray-900">{reasonLabel}</p></div>
        <div><span className="text-gray-500">Gewenste einddatum</span><p className="font-medium text-gray-900">{formatTerminationDate(tr.requestedEndDate)}</p></div>
        {tr.counterProposedEndDate && (
          <div><span className="text-gray-500">Tegenvoorstel datum</span><p className="font-medium text-amber-700">{formatTerminationDate(tr.counterProposedEndDate)}</p></div>
        )}
        {tr.reasonText && (
          <div className="col-span-2"><span className="text-gray-500">Toelichting</span><p className="font-medium text-gray-800">{tr.reasonText}</p></div>
        )}
      </div>

      {needsResponse && !actionMode && (
        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => setActionMode('accept')} className="flex flex-col items-center p-3 rounded-xl border-2 border-green-200 bg-green-50 hover:bg-green-100 transition-colors">
            <CheckCircle className="w-6 h-6 text-green-600 mb-1" /><span className="text-sm font-semibold text-green-800">Akkoord</span>
          </button>
          <button onClick={() => setActionMode('reject')} className="flex flex-col items-center p-3 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 transition-colors">
            <XCircle className="w-6 h-6 text-red-600 mb-1" /><span className="text-sm font-semibold text-red-800">Weigeren</span>
          </button>
          <button onClick={() => setActionMode('counter_propose')} className="flex flex-col items-center p-3 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors">
            <Calendar className="w-6 h-6 text-amber-600 mb-1" /><span className="text-sm font-semibold text-amber-800">Tegenvoorstel</span>
          </button>
        </div>
      )}

      {actionMode === 'accept' && (
        <div className="p-4 rounded-xl border border-green-300 bg-green-50 space-y-3">
          <p className="text-sm text-green-800">
            Bevestig de opzegging per <strong>{formatTerminationDate(tr.counterProposedEndDate || tr.requestedEndDate)}</strong>.
            {' Het contract wordt daarna als aflopend gemarkeerd.'}
          </p>
          <div className="flex space-x-3">
            <button onClick={() => setActionMode(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Annuleren</button>
            <button onClick={() => handleAction('accept')} disabled={processing} className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50">
              {processing ? 'Bezig...' : 'Bevestig akkoord'}
            </button>
          </div>
        </div>
      )}

      {actionMode === 'reject' && (
        <div className="p-4 rounded-xl border border-red-300 bg-red-50 space-y-3">
          <p className="text-sm text-red-800">Geef een reden waarom je het voorstel afwijst.</p>
          <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reden voor afwijzing..."
            className="w-full h-20 p-3 border border-red-300 rounded-lg text-sm resize-none bg-white" />
          <div className="flex space-x-3">
            <button onClick={() => { setActionMode(null); setRejectReason(''); }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Annuleren</button>
            <button onClick={() => handleAction('reject')} disabled={processing || !rejectReason.trim()} className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50">
              {processing ? 'Bezig...' : 'Weigeren'}
            </button>
          </div>
        </div>
      )}

      {actionMode === 'counter_propose' && (
        <div className="p-4 rounded-xl border border-amber-300 bg-amber-50 space-y-3">
          <p className="text-sm text-amber-800">Stel een andere einddatum voor.</p>
          <input type="date" value={counterDate} onChange={(e) => setCounterDate(e.target.value)} className="w-full p-2 border border-amber-300 rounded-lg text-sm bg-white" />
          <textarea value={counterReason} onChange={(e) => setCounterReason(e.target.value)} placeholder="Toelichting (optioneel)..."
            className="w-full h-16 p-2 border border-amber-300 rounded-lg text-sm resize-none bg-white" />
          <div className="flex space-x-3">
            <button onClick={() => { setActionMode(null); setCounterDate(''); setCounterReason(''); }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Annuleren</button>
            <button onClick={() => handleAction('counter_propose')} disabled={processing || !counterDate} className="px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm disabled:opacity-50">
              {processing ? 'Bezig...' : 'Tegenvoorstel verzenden'}
            </button>
          </div>
        </div>
      )}

      <button onClick={() => navigate(`/contract/${contractId}`)} className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
        Bekijk volledige details <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

function TenantArchivedSection({ archivedProperties }) {
  const [openPropertyId, setOpenPropertyId] = useState(null);

  if (!archivedProperties || archivedProperties.length === 0) return null;

  const openEntry = openPropertyId
    ? archivedProperties.find((e) => e.property.id === openPropertyId)
    : null;

  if (openEntry) {
    return (
      <div className="mt-12">
        <button
          onClick={() => setOpenPropertyId(null)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="font-semibold">Terug naar archief</span>
        </button>
        <TenantArchivedDetail property={openEntry.property} contract={openEntry.contract} />
      </div>
    );
  }

  return (
    <div className="mt-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
          <Archive className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Archief</span>
          <span className="text-xs font-medium text-slate-500 bg-slate-200 rounded-full px-2 py-0.5">
            {archivedProperties.length}
          </span>
        </div>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {archivedProperties.map((entry) => (
          <TenantArchivedCard
            key={entry.property.id}
            property={entry.property}
            contract={entry.contract}
            onClick={() => setOpenPropertyId(entry.property.id)}
          />
        ))}
      </div>
    </div>
  );
}

function TenantArchivedCard({ property, contract, onClick }) {
  const status = getContractDisplayStatus(contract);

  const endDate = contract.endDate instanceof Date
    ? contract.endDate
    : (contract.endDate?.toDate ? contract.endDate.toDate() : null);

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden opacity-75 hover:opacity-100 transition-all hover:shadow-md relative text-left w-full cursor-pointer group"
    >
      <div className="absolute inset-0 bg-slate-500 opacity-5 z-0 pointer-events-none" />

      <div className="absolute top-3 left-3 z-20">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-700 border border-slate-300">
          <Archive className="w-3.5 h-3.5" />
          Gearchiveerd
        </span>
      </div>

      <div className="absolute top-3 right-3 z-20">
        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${status.bg}`}>
          {status.label}
        </span>
      </div>

      <div className="relative h-44">
        <PropertyImage
          property={property}
          className="w-full h-full object-cover"
          width={400}
          height={300}
        />
      </div>

      <div className="p-5 relative z-10">
        <h3 className="text-base font-semibold text-slate-800 mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
          {property.titel || property.adres || 'Pand'}
        </h3>

        <div className="flex items-center text-slate-500 mb-1 text-sm">
          <MapPin className="w-3.5 h-3.5 mr-1" />
          <span>{property.adres ? `${property.adres}, ${property.postcode || ''} ${property.stad || ''}` : (property.stad || '—')}</span>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center text-slate-600 font-medium">
            <Euro className="w-3.5 h-3.5 mr-1" />
            <span>€{contract.monthlyRent}/maand</span>
          </div>
          {endDate && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Einddatum: {endDate.toLocaleDateString('nl-BE')}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">Contract beëindigd</span>
          <span className="text-xs font-medium text-blue-500 group-hover:text-blue-600 flex items-center gap-1">
            Bekijk details <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </button>
  );
}

function TenantArchivedDetail({ property, contract }) {
  const { user } = useAuth();
  const [terminationRequest, setTerminationRequest] = useState(null);
  const [loadingTR, setLoadingTR] = useState(true);

  useEffect(() => {
    if (!contract?.id || !user?.uid) { setLoadingTR(false); return; }

    const loadTermination = async () => {
      try {
        const trCol = collection(db, 'terminationRequests');
        const [s1, s2] = await Promise.all([
          getDocs(query(trCol, where('contractId', '==', contract.id), where('initiatorUserId', '==', user.uid))),
          getDocs(query(trCol, where('contractId', '==', contract.id), where('counterpartyUserId', '==', user.uid))),
        ]);
        const seen = new Set();
        const all = [];
        for (const snap of [s1, s2]) {
          for (const d of snap.docs) {
            if (!seen.has(d.id)) { seen.add(d.id); all.push({ id: d.id, ...d.data() }); }
          }
        }
        if (all.length > 0) {
          all.sort((a, b) => {
            const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return tb - ta;
          });
          const completed = all.find((t) => t.status === 'completed' || t.status === 'mutual_acceptance_reached');
          setTerminationRequest(completed || all[0]);
        }
      } catch (err) {
        console.error('Error loading termination request:', err);
      } finally {
        setLoadingTR(false);
      }
    };

    loadTermination();
  }, [contract?.id, user?.uid]);

  const fmtDate = (val) => {
    if (!val) return '—';
    const d = val.toDate ? val.toDate() : (val instanceof Date ? val : new Date(val));
    return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const startDate = contract.startDate instanceof Date ? contract.startDate
    : (contract.startDate?.toDate ? contract.startDate.toDate() : null);
  const endDate = contract.endDate instanceof Date ? contract.endDate
    : (contract.endDate?.toDate ? contract.endDate.toDate() : null);

  const reasonLabel = (() => {
    if (!terminationRequest?.reasonCode) return null;
    const role = terminationRequest.initiatorRole || 'tenant';
    const options = TERMINATION_REASON_OPTIONS[role] || TERMINATION_REASON_OPTIONS.tenant;
    const match = options.find((o) => o.value === terminationRequest.reasonCode);
    return match?.label || terminationRequest.reasonCode;
  })();

  return (
    <div className="space-y-6">
      {/* Property header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="relative h-52">
          <PropertyImage property={property} className="w-full h-full object-cover" width={800} height={400} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <h2 className="text-xl font-bold text-white mb-1">{property.titel || property.adres || 'Pand'}</h2>
            <div className="flex items-center text-white/80 text-sm">
              <MapPin className="w-3.5 h-3.5 mr-1" />
              <span>{property.adres ? `${property.adres}, ${property.postcode || ''} ${property.stad || ''}` : (property.stad || '—')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section 1: Landlord contact */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-blue-500" />
          Contactgegevens verhuurder
        </h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Naam</p>
              <p className="text-sm font-medium text-slate-800">{contract.landlordName || '—'}</p>
            </div>
          </div>
          {contract.landlordEmail && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400">E-mail</p>
                <a href={`mailto:${contract.landlordEmail}`} className="text-sm font-medium text-blue-600 hover:underline">
                  {contract.landlordEmail}
                </a>
              </div>
            </div>
          )}
          {contract.landlordPhone && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Telefoon</p>
                <a href={`tel:${contract.landlordPhone}`} className="text-sm font-medium text-blue-600 hover:underline">
                  {contract.landlordPhone}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section 2: Contract overview */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4 text-emerald-500" />
          Huurcontract
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Huurprijs</p>
            <p className="text-sm font-semibold text-slate-800">€{contract.monthlyRent}/maand</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Waarborg</p>
            <p className="text-sm font-semibold text-slate-800">€{contract.deposit || 0}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Startdatum</p>
            <p className="text-sm font-medium text-slate-700">{startDate ? fmtDate(startDate) : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Einddatum</p>
            <p className="text-sm font-medium text-slate-700">{endDate ? fmtDate(endDate) : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Betaalmethode</p>
            <p className="text-sm font-medium text-slate-700">
              {contract.paymentMethod === 'bank_transfer' ? 'Overschrijving' : (contract.paymentMethod || '—')}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Contract ID</p>
            <p className="text-xs font-mono text-slate-500">{contract.id}</p>
          </div>
        </div>
      </div>

      {/* Section 3: Termination agreement */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-500" />
          Opzeggingsakkoord
        </h3>

        {loadingTR ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400" />
            Laden...
          </div>
        ) : terminationRequest ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Datum aanvraag</p>
                <p className="text-sm font-medium text-slate-700">{fmtDate(terminationRequest.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Datum akkoord</p>
                <p className="text-sm font-medium text-slate-700">
                  {fmtDate(terminationRequest.acceptedByLandlordAt || terminationRequest.acceptedByTenantAt || terminationRequest.completedAt)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Afgesproken einddatum</p>
                <p className="text-sm font-semibold text-red-600">{fmtDate(terminationRequest.effectiveEndDate)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Status</p>
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${
                  TERMINATION_STATUS_STYLES[terminationRequest.status] || 'bg-slate-100 text-slate-700'
                }`}>
                  {TERMINATION_STATUS_LABELS[terminationRequest.status] || terminationRequest.status}
                </span>
              </div>
            </div>

            {reasonLabel && (
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Reden</p>
                <p className="text-sm font-medium text-slate-700">{reasonLabel}</p>
                {terminationRequest.reasonText && (
                  <p className="text-sm text-slate-500 mt-1">{terminationRequest.reasonText}</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Aangevraagd door</p>
                <p className="text-sm font-medium text-slate-700">
                  {terminationRequest.initiatorRole === 'tenant' ? 'Huurder' : 'Verhuurder'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Aanvraag ID</p>
                <p className="text-xs font-mono text-slate-500">{terminationRequest.id}</p>
              </div>
            </div>

            {/* Audit trail */}
            {terminationRequest.auditTrail && terminationRequest.auditTrail.length > 0 && (
              <div className="border-t border-slate-100 pt-4 mt-4">
                <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Tijdlijn</p>
                <div className="space-y-2">
                  {terminationRequest.auditTrail.map((entry, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-slate-600">
                          <span className="font-medium">
                            {entry.action === 'request_created' ? 'Aanvraag ingediend'
                              : entry.action === 'accepted_by_tenant' ? 'Geaccepteerd door huurder'
                              : entry.action === 'accepted_by_landlord' ? 'Geaccepteerd door verhuurder'
                              : entry.action === 'counter_proposed' ? 'Tegenvoorstel ingediend'
                              : entry.action === 'rejected' ? 'Afgewezen'
                              : entry.action === 'withdrawn' ? 'Ingetrokken'
                              : entry.action === 'viewed' ? 'Bekeken'
                              : entry.action}
                          </span>
                        </p>
                        <p className="text-[10px] text-slate-400">{entry.at ? new Date(entry.at).toLocaleString('nl-BE') : '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-slate-50 rounded-xl p-3 mt-2">
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Dit document dient als bewijs van de beëindigingsovereenkomst tussen huurder en verhuurder.
                Alle data (aanvraagdatum, akkoord, partijen, contract ID) worden bewaard conform de wettelijke vereisten.
              </p>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-400 py-4">
            Geen opzeggingsverzoek gevonden voor dit contract.
          </div>
        )}
      </div>
    </div>
  );
}
